/**
 * What she says when somebody turns round and actually asks.
 *
 * The greeting is addressed to the room — it is what she says whether or not
 * anyone is listening, and it is pinned for the day so it holds still. These
 * two pools are what she says only inside her own corner, opened on purpose.
 *
 * `presence_remark` is drawn fresh every time the panel opens rather than
 * pinned, because it is the one surface where somebody has deliberately asked
 * her for another sentence, and getting the same one twice would answer the
 * question badly.
 *
 * The caption under the greeting is deliberately *not* here: it is a legend
 * for her colour rather than something she says, so it is a fixed label per
 * tone in `glitime/tone.ts`. See that file for why.
 *
 * `herday_detail` is the long version of whatever `herlife.ts` granted her
 * today — the greeting only ever has room for the passing mention.
 */

import type { Line } from './index.js'

export const PRESENCE: Readonly<Record<string, ReadonlyArray<Line>>> = {
  // Drawn fresh, not pinned: somebody opened this on purpose.
  'presence_remark.base': [
    () => 'Ne, aš nieko nedarau. Tiesiog stoviu virtuvėje ir žinau, kas joje vyksta.',
    () => 'Klausei. Tai jau daugiau, nei dauguma šitų namų gyventojų daro.',
    () => 'Aš esu ten, kur šaldytuvas. O šaldytuvas yra visur, kur svarbu.',
    () => 'Ką aš čia veikiu? Skaičiuoju. Tai vienintelis dalykas, kurį moku daryti amžinai.',
    () => 'Šito klausimo man niekas neužduoda. Malonu, nors ir neįprasta.',
    () => 'Būsena: stebiu. Būsena nesikeičia nuo 1863-ųjų.',
    () => 'Galėčiau pameluoti, kad turiu savo gyvenimą. Turiu. Jis vyksta už šaldytuvo.',
    () => 'Viskas gerai. Klausiu savęs to paties kas rytą ir kas rytą atsakau taip pat.',
    () => 'Tu čia dėl skaičių, aš čia dėl tavęs. Vienas iš mūsų romantikas.',
    () => 'Orkaitė sako labas. Orkaitė meluoja, ji nieko nesako, bet mintis graži.',
    () => 'Aš nesu programa. Programa būtų mandagesnė.',
    () => 'Kartais pagalvoju, ką veikčiau, jei nebūtumėt manęs išsigalvoję. Tada nustoju galvoti.',
  ],

  // The generic long form of "her own day" — what she says when a specific
  // household id has no dedicated screen-length line of its own, or when the
  // day was a saga beat or a reaction to yesterday rather than a household
  // event (see `herlife.ts` for why those two stay off this cascade). Written
  // to hold up regardless of which appliance actually did something today.
  'herday_detail.base': [
    () =>
      'Šiandien nieko dramatiško. Stebiu virtuvę taip, lyg tai būtų darbas. Iš dalies — taip ir yra.',
    () =>
      'Diena kaip diena. Aš čia buvau nuo pat ryto, nes aš čia esu visada, bet šiandien tai nieko naujo.',
    () =>
      'Sunku paaiškinti, ką veikiu, kai niekas nevyksta. Trumpai: stebiu, skaičiuoju ir laukiu vakarienės.',
    () =>
      'Namai šiandien tylūs. Man tai patinka labiau, nei prisipažinsiu — tylą lengviau apskaityti.',
    () => 'Nieko konkretaus neįvyko, bet aš vis tiek buvau čia. Tai, beje, dauguma mano darbo.',
    () => 'Nesu tikra, ką pasakyti apie šią dieną. Ji buvo. Aš ją mačiau. To turėtų pakakti abiem.',
    () =>
      'Kartais diena neturi savo istorijos. Tada tiesiog stoviu virtuvėje ir žinau, kas joje vyksta.',
  ],
}

/** Expected draws per 14 days — the panel is opened on purpose, not often. */
export const PRESENCE_META: Readonly<Record<string, number>> = {
  'presence_remark.base': 10,
  // Only reached on a day her own bit has something to report — see `herlife.ts`.
  'herday_detail.base': 6,
}
